import { useMemo } from 'react'
import { Bar } from 'react-chartjs-2'
import type { ChartData, ChartOptions } from 'chart.js'
import { ensureChartsRegistered } from './register'
import { CHART_COLORS, useDarkMode } from './palette'

ensureChartsRegistered()

export interface BarSeries {
  label: string
  values: Array<number | null>
  color?: string
  /** Optional per-bar colors; overrides `color` when provided. */
  perBarColors?: string[]
}

export interface BarChartProps {
  labels: string[]
  series: BarSeries[]
  height?: number | string
  logarithmic?: boolean
  barThickness?: number
  rotateDataLabels?: boolean
  hideDataLabels?: boolean
  showLegend?: boolean
  legendPosition?: 'top' | 'bottom' | 'left' | 'right'
  formatValue?: (value: number) => string
  formatTooltip?: (label: string, value: number) => string
  onBarClick?: (label: string, value: number, datasetIndex: number) => void
  className?: string
  ariaLabel?: string
}

const DEFAULT_FORMAT = (v: number) => {
  if (v >= 1000) return '$' + (v / 1000).toFixed(1) + 'k'
  return '$' + Math.round(v)
}

export function BarChart({
  labels,
  series,
  height = 280,
  logarithmic = false,
  barThickness = 18,
  rotateDataLabels = false,
  hideDataLabels = false,
  showLegend = false,
  legendPosition = 'bottom',
  formatValue = DEFAULT_FORMAT,
  formatTooltip,
  onBarClick,
  className,
  ariaLabel,
}: BarChartProps) {
  const dark = useDarkMode()

  const data = useMemo<ChartData<'bar'>>(
    () => ({
      labels,
      datasets: series.map((s) => ({
        label: s.label,
        data: s.values,
        backgroundColor: s.perBarColors ?? s.color ?? CHART_COLORS.primary,
        borderRadius: 4,
        barThickness,
      })),
    }),
    [labels, series, barThickness],
  )

  const options = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: rotateDataLabels ? 50 : 10 } },
      onClick: onBarClick
        ? (_evt, elements, chart) => {
            if (!elements.length) return
            const el = elements[0]
            const label = String(chart.data.labels?.[el.index] ?? '')
            const rawVal = chart.data.datasets[el.datasetIndex]?.data?.[el.index]
            const value = typeof rawVal === 'number' ? rawVal : Number(rawVal) || 0
            onBarClick(label, value, el.datasetIndex)
          }
        : undefined,
      plugins: {
        legend: { display: showLegend, position: legendPosition },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: formatTooltip
            ? {
                label: (ctx) => {
                  const raw = typeof ctx.raw === 'number' ? ctx.raw : 0
                  return formatTooltip(ctx.dataset.label ?? '', raw)
                },
              }
            : undefined,
        },
        datalabels: hideDataLabels
          ? { display: false }
          : {
              display: true,
              align: 'end',
              anchor: 'end',
              offset: 4,
              clip: false,
              rotation: rotateDataLabels ? -45 : 0,
              color: CHART_COLORS.muted,
              font: { size: 10, weight: 500 },
              formatter: (value: unknown) => {
                const n = typeof value === 'number' ? value : Number(value)
                if (!Number.isFinite(n) || n === 0) return ''
                return formatValue(n)
              },
            },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: dark ? CHART_COLORS.tickDark : CHART_COLORS.tickLight, font: { size: 11 } },
        },
        y: logarithmic
          ? {
              type: 'logarithmic' as const,
              beginAtZero: true,
              border: { display: false },
              grid: { color: dark ? CHART_COLORS.gridDark : CHART_COLORS.gridLight },
              ticks: {
                color: dark ? CHART_COLORS.tickDark : CHART_COLORS.tickLight,
                font: { size: 10 },
                maxTicksLimit: 5,
                callback: (value: number | string) => {
                  const n = typeof value === 'number' ? value : Number(value)
                  if ([10, 100, 1000, 10000, 100000].includes(n)) return '$' + n.toLocaleString()
                  return null
                },
              },
            }
          : {
              beginAtZero: true,
              grid: { color: dark ? CHART_COLORS.gridDark : CHART_COLORS.gridLight },
              ticks: { color: dark ? CHART_COLORS.tickDark : CHART_COLORS.tickLight },
            },
      },
    }),
    [dark, logarithmic, rotateDataLabels, hideDataLabels, showLegend, legendPosition, formatValue, formatTooltip, onBarClick],
  )

  return (
    <div
      className={className}
      style={{ height, width: '100%' }}
      role="img"
      aria-label={ariaLabel}
    >
      <Bar data={data} options={options} />
    </div>
  )
}
